/**
 * Calendar and Date Utilities
 *
 * Shared utilities for date formatting, date range calculations,
 * and common date operations across the application.
 */

/**
 * Format a Date object as YYYY-MM-DD
 * @param {Date} date - The date to format
 * @returns {string} - Formatted date string
 */
export const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse a YYYY-MM-DD string to a Date object
 * @param {string} dateString - The date string to parse
 * @returns {Date} - Parsed date object
 */
export const parseDate = (dateString) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Get today's date as YYYY-MM-DD
 * @returns {string} - Today's date
 */
export const getToday = () => {
  return formatDate(new Date());
};

/**
 * Get date range for a preset period
 * @param {string} preset - Preset name: 'today', 'yesterday', '1week', '2weeks', '1month', '3months', '6months', '1year'
 * @returns {object} - Object with startDate and endDate as YYYY-MM-DD strings
 */
export const getDateRangePreset = (preset) => {
  const today = new Date();
  const endDate = formatDate(today);
  let startDate;

  switch (preset) {
    case 'today':
      startDate = endDate;
      break;
    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      startDate = formatDate(yesterday);
      return { startDate, endDate: startDate };
    }
    case '1week': {
      const oneWeekAgo = new Date(today);
      oneWeekAgo.setDate(today.getDate() - 7);
      startDate = formatDate(oneWeekAgo);
      break;
    }
    case '2weeks': {
      const twoWeeksAgo = new Date(today);
      twoWeeksAgo.setDate(today.getDate() - 14);
      startDate = formatDate(twoWeeksAgo);
      break;
    }
    case '1month': {
      const oneMonthAgo = new Date(today);
      oneMonthAgo.setMonth(today.getMonth() - 1);
      startDate = formatDate(oneMonthAgo);
      break;
    }
    case '3months': {
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(today.getMonth() - 3);
      startDate = formatDate(threeMonthsAgo);
      break;
    }
    case '6months': {
      const sixMonthsAgo = new Date(today);
      sixMonthsAgo.setMonth(today.getMonth() - 6);
      startDate = formatDate(sixMonthsAgo);
      break;
    }
    case '1year': {
      const oneYearAgo = new Date(today);
      oneYearAgo.setFullYear(today.getFullYear() - 1);
      startDate = formatDate(oneYearAgo);
      break;
    }
    default:
      throw new Error(`Unknown preset: ${preset}`);
  }

  return { startDate, endDate };
};

/**
 * Calculate the difference in days between two dates
 * @param {string|Date} date1 - First date
 * @param {string|Date} date2 - Second date
 * @returns {number} - Number of days between dates
 */
export const daysBetween = (date1, date2) => {
  const d1 = typeof date1 === 'string' ? parseDate(date1) : date1;
  const d2 = typeof date2 === 'string' ? parseDate(date2) : date2;
  const diffTime = Math.abs(d2 - d1);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Check if a date is valid
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidDate = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};

/**
 * Validate that end date is greater than or equal to start date
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {boolean} - True if end date >= start date, false otherwise
 */
export const isValidDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return false;
  const start = typeof startDate === 'string' ? parseDate(startDate) : startDate;
  const end = typeof endDate === 'string' ? parseDate(endDate) : endDate;
  return end >= start;
};

/**
 * Get the start and end of a month
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @returns {object} - Object with startDate and endDate
 */
export const getMonthRange = (year, month) => {
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0);
  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate)
  };
};

/**
 * Get the start and end of current month
 * @returns {object} - Object with startDate and endDate
 */
export const getCurrentMonthRange = () => {
  const today = new Date();
  return getMonthRange(today.getFullYear(), today.getMonth() + 1);
};

/**
 * Available date range presets with labels
 */
export const DATE_RANGE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '1week', label: '1 Week' },
  { value: '2weeks', label: '2 Weeks' },
  { value: '1month', label: '1 Month' },
  { value: '3months', label: '3 Months' },
  { value: '6months', label: '6 Months' },
  { value: '1year', label: '1 Year' }
];

/**
 * Get filtered date range presets
 * @param {string[]|string} includePresets - Optional array of preset values to include (e.g., ['today', '1week', '1month']), or 'all' to return all presets. If not provided, returns all presets.
 * @returns {array} - Filtered presets array
 */
export const getFilteredPresets = (includePresets) => {
  if (!includePresets || includePresets === 'all') return DATE_RANGE_PRESETS;
  return DATE_RANGE_PRESETS.filter(preset => includePresets.includes(preset.value));
};
